import React, {
  FunctionComponent,
  ReactNode,
  useEffect,
  useState
} from "react";

import { useSelector } from "react-redux";
import { asList, useLoadIfEmpty } from "../../hooks/useLoadIfEmpty";
import { thunkFetchPaymentProviders } from "../../store/paymentProvider/actions";
import {
  Checkbox,
  FormControl,
  IconButton,
  Input,
  InputLabel,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select,
  SelectProps
} from "@material-ui/core";
import { AppState } from "../../store";
import { PaymentProvider } from "../../models/Tenant";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import Divider from "@material-ui/core/Divider";

interface PaymentProviderMultiSelectProps extends Omit<SelectProps, "loading"> {
  label?: string;
  onSelected: (value: PaymentProvider[]) => void;
  variant?: "filled" | "outlined" | "standard";
  value: Array<PaymentProvider>;
  onlyAllowsIdSwitching?: boolean;
}

export const PaymentProviderMultiSelect: FunctionComponent<
  PaymentProviderMultiSelectProps
> = ({
  label = "PaymentProvider",
  onSelected,
  onChange,
  variant = "standard",
  value = [] as Array<PaymentProvider>,
  onlyAllowsIdSwitching,
  ...props
}) => {
  const paymentProviders = asList<PaymentProvider>(
    useSelector<AppState, unknown>(s => s.paymentProviders)
  );

  useLoadIfEmpty(!paymentProviders.length, thunkFetchPaymentProviders);

  const handleChange = (
    event: React.ChangeEvent<{ name?: string; value: unknown }>,
    child: React.ReactNode
  ) => {
    if (onSelected) {
      let providers = event.target.value as Array<PaymentProvider>;

      const notCombinableProvider = providers.find(x => !x.canBeCombined);
      providers = notCombinableProvider ? [notCombinableProvider] : providers;

      providers.forEach((x, i) => (x.displayOrder = i + 1));
      onSelected(providers ?? []);
    }
    if (onChange) {
      onChange(event, child);
    }
  };

  const renderNames = (ids: ReadonlyArray<PaymentProvider>): ReactNode =>
    ids.map(x => x.name).join(", ");

  const handleDown =
    (position: number) => (event: { stopPropagation: () => void }) => {
      event.stopPropagation();
      value[position] = value.splice(position + 1, 1, value[position])[0];
      value.forEach((x, i) => (x.displayOrder = i + 1));
      onSelected(value);
    };
  const handleUp =
    (position: number) => (event: { stopPropagation: () => void }) => {
      event.stopPropagation();
      value[position] = value.splice(position - 1, 1, value[position])[0];
      value.forEach((x, i) => (x.displayOrder = i + 1));
      onSelected(value);
    };

  return (
    <FormControl
      style={props.style}
      variant={variant}
      className={props.className}
    >
      <InputLabel
        id="loginMethod-multi-select-label"
        style={
          variant === "outlined"
            ? {
                backgroundColor: "#fff",
                paddingLeft: "0.5rem",
                paddingRight: "0.5rem"
              }
            : undefined
        }
      >
        {label}
      </InputLabel>
      <Select
        multiple
        value={value}
        onChange={handleChange}
        labelId="loginMethod-multi-select-label"
        id="loginMethod-multi-select"
        autoWidth={true}
        input={<Input />}
        disabled={props.disabled}
        renderValue={value => renderNames(value as PaymentProvider[])}
      >
        {value
          .sort((a, b) => a.displayOrder - b.displayOrder)
          .map((provider, i) => (
            <MenuItem key={i} value={provider as any} disableRipple>
              <Checkbox checked />
              <ListItemText
                primary={provider.name}
                secondary={!provider.canBeCombined ? "Can't be combined" : ""}
              />
              <Divider
                orientation="vertical"
                flexItem
                variant="middle"
                style={{ margin: "0 5px" }}
              />
              <ListItemIcon
                onClick={e => e.stopPropagation()}
                style={{ minWidth: 0 }}
              >
                <IconButton
                  style={{ minWidth: 0 }}
                  onClick={handleUp(i)}
                  disabled={i === 0}
                  edge="end"
                >
                  <ArrowUpwardIcon />
                </IconButton>
              </ListItemIcon>
              <ListItemIcon
                onClick={e => e.stopPropagation()}
                style={{ minWidth: 0 }}
              >
                <IconButton
                  style={{ minWidth: 0 }}
                  onClick={handleDown(i)}
                  disabled={i === value.length - 1}
                  edge="end"
                >
                  <ArrowDownwardIcon />
                </IconButton>
              </ListItemIcon>
            </MenuItem>
          ))}
        <ListSubheader>Disabled</ListSubheader>
        {paymentProviders
          .filter(x => value.every(v => v.id !== x.id))
          .map(x => (
            <MenuItem key={x.id} value={x as any} disableRipple>
              <Checkbox checked={false} />
              <ListItemText
                primary={x.name}
                secondary={!x.canBeCombined ? "Can't be combined" : ""}
              />
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  );
};
